/**
 * Step 1 — Client, dates and account owner.
 *
 * The only genuinely new information a proposal needs. Everything else on this
 * screen is either derived (valid-until) or prefilled from the signed-in user.
 *
 * The client-context section is collapsed by default and clearly labelled as
 * internal: it is captured for the executive-summary conversation, review and
 * future AI assistance, and never adds a section to the Word document.
 */
import { useState } from 'react';
import type {
  AIStatus,
  ClientContext,
  PaymentTerm,
  ProposalConfiguration,
} from '../../lib/api';
import { Card, Field, Input, Select, formatDate } from '../../components/ui';
import { AIAssist } from './AIAssist';

const VALIDITY_OPTIONS = [14, 30, 45, 60, 90];

export interface StepProps {
  config: ProposalConfiguration;
  update<K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]): void;
  disabled: boolean;
  /** Configuration paths the quality check flagged, for highlighting. */
  flagged: Set<string>;
  paymentTerms: PaymentTerm[];
  proposalId: string;
  /** Null, or a disabled provider, means no AI affordances render anywhere. */
  aiStatus: AIStatus | null;
}

/** Valid-until, derived unless explicitly overridden. */
export function resolveValidityDate(config: ProposalConfiguration): string {
  if (config.validityDate) return config.validityDate;
  if (!config.proposalDate) return '';
  const date = new Date(`${config.proposalDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + (config.validityDays ?? 30));
  return date.toISOString().slice(0, 10);
}

export function ClientStep({
  config,
  update,
  disabled,
  flagged,
  proposalId,
  aiStatus,
}: StepProps) {
  const [contextOpen, setContextOpen] = useState(
    () => Object.values(config.clientContext ?? {}).some(Boolean),
  );

  const derivedValidity = resolveValidityDate(config);
  const isOverridden = Boolean(config.validityDate);

  /** The notes as one block, which is what a tidy-up or an outcome pass reads. */
  const notesText = Object.values(config.clientContext ?? {})
    .filter(Boolean)
    .join('\n');

  const updateContext = (key: keyof ClientContext, value: string) =>
    update('clientContext', { ...(config.clientContext ?? {}), [key]: value });

  const err = (path: string) => (flagged.has(path) ? 'Required' : undefined);

  return (
    <div className="space-y-6">
      <Card title="Client" description="Who the proposal is for.">
        <Field
          label="Client company name"
          htmlFor="clientCompanyName"
          required
          error={err('clientCompanyName')}
        >
          <Input
            id="clientCompanyName"
            value={config.clientCompanyName ?? ''}
            maxLength={120}
            disabled={disabled}
            autoFocus
            placeholder="e.g. Northwind Manufacturing Corp."
            onChange={(event) => update('clientCompanyName', event.target.value)}
          />
        </Field>
      </Card>

      <Card title="Proposal" description="Dating and how long the offer stands.">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Proposal date" htmlFor="proposalDate" required error={err('proposalDate')}>
            <Input
              id="proposalDate"
              type="date"
              value={config.proposalDate ?? ''}
              disabled={disabled}
              onChange={(event) => update('proposalDate', event.target.value)}
            />
          </Field>

          <Field label="Validity period" htmlFor="validityDays" required>
            <Select
              id="validityDays"
              value={String(config.validityDays ?? 30)}
              disabled={disabled || isOverridden}
              onChange={(event) => update('validityDays', Number(event.target.value))}
            >
              {VALIDITY_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Valid until"
            htmlFor="validityDate"
            hint={
              isOverridden
                ? 'Overridden.'
                : derivedValidity
                  ? `Calculated: ${formatDate(derivedValidity)}`
                  : 'Set a proposal date first.'
            }
            error={err('validityDate')}
          >
            <div className="flex gap-2">
              <Input
                id="validityDate"
                type="date"
                value={derivedValidity}
                disabled={disabled}
                onChange={(event) => update('validityDate', event.target.value)}
              />
              {isOverridden && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => update('validityDate', undefined)}
                  className="shrink-0 rounded-md px-2 text-[12px] font-medium text-teal hover:bg-tint"
                >
                  Reset
                </button>
              )}
            </div>
          </Field>
        </div>
      </Card>

      <Card
        title="Account owner"
        description="Printed on the cover page. Prefilled from your profile."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Account owner name"
            htmlFor="accountOwnerName"
            required
            error={err('accountOwnerName')}
          >
            <Input
              id="accountOwnerName"
              value={config.accountOwnerName ?? ''}
              maxLength={80}
              disabled={disabled}
              onChange={(event) => update('accountOwnerName', event.target.value)}
            />
          </Field>

          <Field
            label="Account owner title"
            htmlFor="accountOwnerTitle"
            required
            error={err('accountOwnerTitle')}
          >
            <Input
              id="accountOwnerTitle"
              value={config.accountOwnerTitle ?? ''}
              maxLength={80}
              disabled={disabled}
              onChange={(event) => update('accountOwnerTitle', event.target.value)}
            />
          </Field>

          <Field
            label="Account owner email"
            htmlFor="accountOwnerEmail"
            hint="Optional. Kept for follow-up — the document prints the company contact line."
          >
            <Input
              id="accountOwnerEmail"
              type="email"
              value={config.accountOwnerEmail ?? ''}
              maxLength={254}
              disabled={disabled}
              onChange={(event) => update('accountOwnerEmail', event.target.value)}
            />
          </Field>

          <Field label="Account owner phone" htmlFor="accountOwnerPhone" hint="Optional.">
            <Input
              id="accountOwnerPhone"
              type="tel"
              value={config.accountOwnerPhone ?? ''}
              maxLength={40}
              disabled={disabled}
              onChange={(event) => update('accountOwnerPhone', event.target.value)}
            />
          </Field>
        </div>

        <p className="mt-4 rounded-md bg-tint px-3 py-2 text-[12px] text-muted">
          The proposal prints the approved company contact line
          (info@lotustech.ph · +63 917 655 0376). Your own details above are stored for
          internal follow-up only.
        </p>
      </Card>

      <section className="rounded-lg border border-rule bg-white">
        <button
          type="button"
          aria-expanded={contextOpen}
          aria-controls="client-context"
          onClick={() => setContextOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
        >
          <span>
            <span className="block text-[15px] font-semibold text-ink">
              Tell us about the opportunity
            </span>
            <span className="mt-0.5 block text-[13px] text-muted">
              Optional. Internal only — never added to the Word document.
            </span>
          </span>
          <span aria-hidden="true" className="shrink-0 text-muted">
            {contextOpen ? '−' : '+'}
          </span>
        </button>

        {contextOpen && (
          <div id="client-context" className="space-y-4 border-t border-rule px-5 py-4">
            {(
              [
                ['clientSituation', 'Client situation', 'Where they are today.'],
                ['businessChallenge', 'Business challenge', 'What is not working.'],
                ['clientObjective', 'Client objective', 'What they are trying to achieve.'],
                ['desiredOutcome', 'Desired outcome', 'What good looks like to them.'],
                ['additionalContext', 'Additional context', 'Anything else worth recording.'],
              ] as Array<[keyof ClientContext, string, string]>
            ).map(([key, label, hint]) => (
              <Field key={key} label={label} htmlFor={key} hint={hint}>
                <textarea
                  id={key}
                  rows={2}
                  maxLength={2000}
                  disabled={disabled}
                  value={config.clientContext?.[key] ?? ''}
                  onChange={(event) => updateContext(key, event.target.value)}
                  className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 disabled:bg-tint"
                />
              </Field>
            ))}

            <p className="rounded-md bg-tint px-3 py-2 text-[12px] text-muted">
              Used for executive-summary customisation and review context. It does not add
              sections to the generated proposal
              {aiStatus?.enabled
                ? ', and only the fields listed on each button are sent to the AI provider.'
                : '.'}
            </p>

            {aiStatus?.enabled && proposalId && (
              <AIAssist
                proposalId={proposalId}
                status={aiStatus}
                label="Optional AI assistance"
                functions={['improveClientContext', 'generateBusinessOutcomes']}
                currentText={notesText}
                disabled={disabled}
                // Applied into "Additional context": the assistant tidies or
                // proposes, and the salesperson keeps the fields they wrote.
                onApply={(text) => updateContext('additionalContext', text)}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
}
